import React, { ReactNode, memo, SyntheticEvent } from 'react'
import classNames from 'classnames'
import { makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { $enum } from 'ts-enum-util'
import uuid from 'uuid/v4'
import { AddIcon, EllipsisIcon } from 'transfix-icons'
import { shiftSelf } from 'utils/shiftSelf'
import { useToggle } from 'utils/useToggle'
import 'scss/5_components/_dropdown.scss'

export enum DropdownIconType {
  ellipsis = 'ellipsis',
  add = 'add',
}

interface IDropdownItemProps {
  label: string
  disabled?: boolean
  itemComponent?: ReactNode
  index: number
  onClick: (index: number) => void
  dataTest?: string
  id?: string
}

const useDropdownStyles = makeStyles(() => ({
  disabledBtn: {
    '&:disabled, &.btn-disabled, &[disabled]': {
      backgroundColor: `${brandColors.white} !important`,
    },
    '.dropdown.overflow-menu &.dropdown-listbox-item *': {
      color: `${brandColors.coolGray5} !important`,
      fill: `${brandColors.coolGray5} !important`,
    },
  },
}))

export const DropdownItem = memo<IDropdownItemProps>(
  ({ dataTest = 'dropdown-item', disabled = false, id, index, itemComponent, label, onClick }) => {
    const clickHandler = (e: SyntheticEvent) => {
      e.stopPropagation()
      e.preventDefault()
      onClick(index)
    }
    const classes = useDropdownStyles()
    return (
      <button
        className={classNames('dropdown-listbox-item', { [classes.disabledBtn]: disabled })}
        data-test={`${dataTest}-${label.toLowerCase()}`}
        onClick={clickHandler}
        onKeyDown={clickHandler}
        role='option'
        tabIndex={-1}
        type='button'
        disabled={disabled}
        aria-selected={false}
        id={id}>
        {itemComponent ?? label}
      </button>
    )
  }
)

export interface IDropdownOption {
  label: string
  disabled?: boolean
  itemComponent?: ReactNode
  action: (value?: string) => void
  dataClipboardText?: string
  id?: string
}

interface IDropdownProps {
  options: IDropdownOption[]
  dropdownId?: string
  dataTest?: string
  icon?: DropdownIconType
}

const DROPDOWN_ICONS = {
  ellipsis: <EllipsisIcon />,
  add: <AddIcon />,
}

export const Dropdown = memo<IDropdownProps>(
  ({ options, dropdownId = uuid(), dataTest = 'dropdown', icon }) => {
    const [expanded, toggle, wrapperRef] = useToggle<HTMLDivElement>()
    const dropdownRef = React.createRef<HTMLDivElement>()

    const clickHandler = (e: SyntheticEvent) => {
      e.stopPropagation()
      shiftSelf(dropdownRef)
      toggle()
    }

    const itemClickHandler = (index: number) => {
      const option = options[index]
      option.action(option.label.toLowerCase())
      toggle()
    }

    return (
      <div className='dropdown' ref={wrapperRef}>
        <button
          className={`${
            icon === $enum(DropdownIconType).getValueOrDefault(DropdownIconType.add)
              ? 'dropdown-button-primary bg-accent-active'
              : 'dropdown-button'
          }`}
          type='button'
          aria-haspopup='listbox'
          id={dropdownId}
          onClick={clickHandler}
          data-test={dataTest}>
          {icon ? DROPDOWN_ICONS[icon] : DROPDOWN_ICONS.ellipsis}
        </button>
        <div
          role='listbox'
          className={`dropdown-listbox ${expanded ? '' : 'dn'}`}
          ref={dropdownRef}>
          {options.map((option, i) => (
            <DropdownItem
              key={`${dropdownId}-${option.label}`}
              label={option.label}
              disabled={option.disabled}
              id={option.id}
              itemComponent={option.itemComponent}
              dataTest={dataTest}
              index={i}
              onClick={itemClickHandler}
            />
          ))}
        </div>
      </div>
    )
  }
)
